import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFinalizeConnect } from "@/hooks/use-zernio";

type State = "working" | "success" | "error";

export function ZernioCallbackPage() {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const finalize = useFinalizeConnect();
  const [state, setState] = useState<State>("working");
  const [message, setMessage] = useState<string>(() => t("zernioCallback.initialMessage"));

  useEffect(() => {
    const stateParam = params.get("state");
    if (!stateParam) {
      setState("error");
      setMessage(t("zernioCallback.missingState"));
      return;
    }
    (async () => {
      try {
        const result = await finalize.mutateAsync(stateParam);
        setState("success");
        setMessage(
          t("zernioCallback.connectedMessage", {
            platform: result.platform.toLowerCase(),
            brandSlug: result.brandSlug,
          }),
        );
        // If in a popup, notify opener and close
        if (window.opener) {
          window.opener.postMessage({ type: "zernio-connected" }, window.location.origin);
          setTimeout(() => window.close(), 800);
        }
      } catch (e: any) {
        setState("error");
        setMessage(e.message ?? t("zernioCallback.failedMessage"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-3">
        <h1 className={`text-2xl font-semibold ${state === "error" ? "text-destructive" : ""}`}>
          {state === "working" && t("zernioCallback.working")}
          {state === "success" && t("zernioCallback.success")}
          {state === "error" && t("zernioCallback.error")}
        </h1>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}
